"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { RiSearchLine, RiUserAddLine } from "react-icons/ri";
import ProviderAvatar from "@/components/provider-avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUser } from "@/contexts/user-provider";
import { useProviders } from "@/hooks/use-providers";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import type { TeamConversationSummary } from "@/types/team-chat";
import { useAddMember, useCreateConversation, useHideConversation } from "@/query/team-chat";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
interface AddMemberPopoverProps {
  conversation: TeamConversationSummary;
}
export function AddMemberPopover(t0) {
  const $ = _c(38);
  const {
    conversation
  } = t0;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      hasFirebaseUid: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    providers: allProviders
  } = useProviders(t1);
  const addMember = useAddMember(conversation.id);
  const createConversation = useCreateConversation();
  const hideConversation = useHideConversation();
  const {
    setActiveConversation
  } = useTeamChatDrawerStore();
  let t2;
  if ($[1] !== conversation.members) {
    t2 = new Set(conversation.members.map(_temp));
    $[1] = conversation.members;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const existingMemberIds = t2;
  let t3;
  if ($[3] !== allProviders || $[4] !== existingMemberIds || $[5] !== search) {
    let t4;
    if ($[7] !== existingMemberIds) {
      t4 = p => !p.isArchived && !existingMemberIds.has(p.id);
      $[7] = existingMemberIds;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== search) {
      t5 = p_0 => {
        if (!search) {
          return true;
        }
        const name = `${p_0.firstName} ${p_0.lastName}`.toLowerCase();
        return name.includes(search.toLowerCase());
      };
      $[9] = search;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t3 = allProviders.filter(t4).filter(t5);
    $[3] = allProviders;
    $[4] = existingMemberIds;
    $[5] = search;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const availableProviders = t3;
  let t4;
  if ($[11] !== addMember || $[12] !== conversation.id || $[13] !== conversation.members || $[14] !== conversation.type || $[15] !== createConversation || $[16] !== currentUser?.id || $[17] !== hideConversation || $[18] !== setActiveConversation) {
    t4 = async providerId => {
      if (conversation.type === "DM") {
        const memberIds = [...conversation.members.filter(m_0 => m_0.providerId !== currentUser?.id).map(_temp2), providerId];
        const result = await createConversation.mutateAsync({
          type: "GROUP",
          memberIds
        });
        if (result?.id) {
          hideConversation.mutate(conversation.id);
          setActiveConversation(result.id);
        }
      } else {
        await addMember.mutateAsync(providerId);
      }
      setOpen(false);
      setSearch("");
    };
    $[11] = addMember;
    $[12] = conversation.id;
    $[13] = conversation.members;
    $[14] = conversation.type;
    $[15] = createConversation;
    $[16] = currentUser?.id;
    $[17] = hideConversation;
    $[18] = setActiveConversation;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const handleSelect = t4;
  const isPending = addMember.isPending || createConversation.isPending || hideConversation.isPending;
  if (conversation.type === "DM" && !hasPermission(currentUser, PERMISSIONS.WHISPER_GROUPS_CREATE)) {
    return null;
  }
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PopoverTrigger asChild={true}><button className="shrink-0 rounded p-1.5 text-foreground-muted transition-colors hover:text-foreground"><RiUserAddLine className="h-4 w-4" /></button></PopoverTrigger>;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <RiSearchLine className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground-muted" />;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_0 => setSearch(e_0.target.value);
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== search) {
    t8 = <div className="border-b border-border p-2"><div className="relative">{t6}<input type="text" placeholder="Add a person..." value={search} onChange={t7} className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-caption text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary" autoFocus={true} /></div></div>;
    $[23] = search;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== availableProviders || $[26] !== handleSelect || $[27] !== isPending || $[28] !== search) {
    t9 = availableProviders.length === 0 ? <p className="px-2 py-3 text-center text-caption text-foreground-muted">{search ? "No results" : "No more team members"}</p> : availableProviders.map(provider => <button key={provider.id} onClick={() => handleSelect(provider.id)} disabled={isPending} className={cx("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-hover disabled:opacity-50")}><ProviderAvatar providerId={provider.id} size={24} /><span className="truncate text-caption text-foreground">{provider.firstName} {provider.lastName}</span></button>);
    $[25] = availableProviders;
    $[26] = handleSelect;
    $[27] = isPending;
    $[28] = search;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t9) {
    t10 = <div className="max-h-48 overflow-y-auto p-1">{t9}</div>;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t8) {
    t11 = <PopoverContent align="end" className="w-64 p-0" onOpenAutoFocus={_temp3}>{t8}{t10}</PopoverContent>;
    $[32] = t10;
    $[33] = t8;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== open || $[36] !== t11) {
    t12 = <Popover open={open} onOpenChange={setOpen}>{t5}{t11}</Popover>;
    $[35] = open;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
}
function _temp3(e) {
  return e.preventDefault();
}
function _temp2(m_1) {
  return m_1.providerId;
}
function _temp(m) {
  return m.providerId;
}